import AWSAppSyncClient, { AUTH_TYPE } from 'aws-appsync';
import {
  PROPERTY_SERVICE_V3_AWS_CONFIG,
  PROPERTY_SERVICE_V4_AWS_CONFIG,
} from '../env/Configurations';
import {
  createTVCategory,
  updateTVCategory,
  deleteTVCategory,
  deleteBouquet,
  reassignTVCategory,
  createCityGuide,
  deleteCityGuide,
  updateCityGuideState,
  updateCityGuide,
  createFeedback,
  updateFeedback,
  deleteLiveChannel,
  createLiveChannel,
  updateLiveChannel,
  activateFeedback,
  deactivateFeedback,
  deleteFeedback,
  changeTVStatus,
  cityGuideCategory,
  updateCityGuidesCategory,
  deleteCityGuidesCategory,
  uploadTvChannelsByCSV,
  uploadBouquetsByCSV,
  uploadCategoriesByCSV,
} from '../graphql/mutations';
import {
  getBouquetDetails,
  getLiveTV,
  getCategory,
  getBouquetsList,
  getCityGuide,
  listFeedback,
  listChannnelCount,
  getCategoryChannel,
  cityGuideCategories,
} from '../graphql/queries';
import { getDefaultTemplate, getEmailServices, getlistEmailTemplateVariables } from '../graphql/email-services/queries';
import { getCurrentJwtToken, getEncodedValue } from './authUtils';
import gql from 'graphql-tag';
import { createBouquet, updateBouquet } from '../graphql/mutations';
import {
  createEmailService,
  deleteEmailServiceTemplate,
  publishEmailServiceTemplate,
  unPublishEmailServiceTemplate,
  updateEmailService,
  updateEmailServiceTemplate,
} from '../graphql/email-services/mutations';
import { propertyServiceV7Client } from './ServicesAPIUtils';

const propertyServiceV3Client = new AWSAppSyncClient({
  url: PROPERTY_SERVICE_V3_AWS_CONFIG.aws_appsync_graphqlEndpoint,
  region: PROPERTY_SERVICE_V3_AWS_CONFIG.region,
  auth: {
    type: AUTH_TYPE.AMAZON_COGNITO_USER_POOLS,
    jwtToken: async () => await getCurrentJwtToken(),
  },
  disableOffline: true,
  cacheOptions: {
    addTypename: false,
  },
});

const propertyServiceV4Client = new AWSAppSyncClient({
  url: PROPERTY_SERVICE_V4_AWS_CONFIG.aws_appsync_graphqlEndpoint,
  region: PROPERTY_SERVICE_V4_AWS_CONFIG.region,
  auth: {
    type: AUTH_TYPE.AMAZON_COGNITO_USER_POOLS,
    jwtToken: async () => await getCurrentJwtToken(),
  },
  disableOffline: true,
  cacheOptions: {
    addTypename: false,
  },
});

export const getBouquetList = async (hotelId, lang) => {
  return await propertyServiceV3Client.query({
    query: gql(getBouquetsList),
    variables: getEncodedValue({
      hotelId,
      lang,
    }),
    fetchPolicy: 'network-only',
  });
};

export const getBouquet = async (hotelId, id, lang) => {
  return await propertyServiceV3Client.query({
    query: gql(getBouquetDetails),
    variables: getEncodedValue({
      hotelId,
      id,
      lang,
    }),
    fetchPolicy: 'network-only',
  });
};

export const deleteBouquetDetail = async (hotelId, id, lang) => {
  return await propertyServiceV3Client.mutate({
    mutation: gql(deleteBouquet),
    variables: getEncodedValue({
      id: id,
      hotelId: hotelId,
      lang: lang,
    }),
  });
};

export const getChannelList = async (hotelId, lang) => {
  return await propertyServiceV3Client.query({
    query: gql(getLiveTV),
    variables: getEncodedValue({
      hotelId,
      lang,
    }),
    fetchPolicy: 'network-only',
  });
};

export const createNewBouquet = async (bouquet, hotelId, lang) => {
  return await propertyServiceV3Client.mutate({
    mutation: gql(createBouquet),
    variables: getEncodedValue({
      name: bouquet.name,
      code: bouquet.code,
      floor: bouquet.floor.map((floor) => floor?.name ?? ''),
      room: bouquet.room.map((room) => room?.name ?? ''),
      location: bouquet.location.map((location) => location?.name ?? ''),
      hotelId: hotelId,
      floorIDs: bouquet.floorIDs,
      locationIds: bouquet.locationIds,
      lang: lang,
    }),
  });
};

export const updateBouquetDetails = async (bouquet, hotelId, lang) => {
  return await propertyServiceV3Client.mutate({
    mutation: gql(updateBouquet),
    variables: getEncodedValue({
      name: bouquet.name,
      code: bouquet.code,
      floor: bouquet.floor.map((floor) => floor?.name ?? ''),
      room: bouquet.room.map((room) => room?.name ?? ''),
      location: bouquet.location.map((location) => location?.name ?? ''),
      hotelId: hotelId,
      floorIDs: bouquet.floorIDs,
      locationIds: bouquet.locationIds,
      id: bouquet.id,
      lang: lang,
    }),
  });
};

export const getCategoryList = async (hotelId, lang) => {
  return await propertyServiceV3Client.query({
    query: gql(getCategory),
    variables: getEncodedValue({
      hotelId,
      lang,
    }),
    fetchPolicy: 'network-only',
  });
};

export const getCategoryChannelList = async (hotelId, categoryID, lang) => {
  return await propertyServiceV4Client.query({
    query: gql(getCategoryChannel),
    variables: getEncodedValue({
      hotelId,
      categoryID,
      lang,
    }),
    fetchPolicy: 'network-only',
  });
};

export const createNewCategory = async (category, hotelId, lang) => {
  return await propertyServiceV3Client.mutate({
    mutation: gql(createTVCategory),
    variables: getEncodedValue({
      code: category.categoryCode,
      name: category.categoryName,
      isActive: category.status,
      hotelId: hotelId,
      lang: lang,
    }),
  });
};

export const updateCategoryDetails = async (category, hotelId, lang) => {
  return await propertyServiceV3Client.mutate({
    mutation: gql(updateTVCategory),
    variables: getEncodedValue({
      id: category.id,
      code: category.code,
      name: category.name,
      isActive: category.status,
      hotelId: hotelId,
      lang: lang,
    }),
  });
};

export const deleteCategoryDetails = async (categoryId, hotelId, lang) => {
  return await propertyServiceV3Client.mutate({
    mutation: gql(deleteTVCategory),
    variables: getEncodedValue({
      id: categoryId,
      hotelId: hotelId,
      lang: lang,
    }),
  });
};

export const assignNewCategory = async (
  newCategoryID,
  oldCategoryID,
  hotelId,
  newCategoryName,
  oldCategoryName,
  lang,
) => {
  return await propertyServiceV3Client.mutate({
    mutation: gql(reassignTVCategory),
    variables: getEncodedValue({
      newCategoryID: newCategoryID,
      oldCategoryID: oldCategoryID,
      oldCategoryName: oldCategoryName,
      newCategoryName: newCategoryName,
      hotelId: hotelId,
      lang: lang,
    }),
  });
};
export const createTVChannel = async (channel, hotelId, lang) => {
  return await propertyServiceV3Client.mutate({
    mutation: gql(createLiveChannel),
    variables: getEncodedValue({
      name: channel.name,
      code: channel.code,
      number: channel.channelNumber,
      categoryId: channel.channelCategoryID,
      categoryName: channel.categoryName,
      language: channel.channelLanguage,
      description: channel.description,
      externalURL: channel.externalURL,
      tvType: channel.tvType,
      status: channel.status,
      isDRM: channel.isDRM,
      isEpgURL: channel.isEpgURL,
      epgURL: channel.epgURL,
      images: channel.images,
      iptv: channel.iptv,
      coaxial: channel.coaxial,
      bouquet: channel.bouquetInput,
      hotelId: hotelId,
      lang: lang,
      pushChannel: channel.pushChannel,
    }),
  });
};

export const updateTVChannel = async (channel, hotelId, lang) => {
  return await propertyServiceV3Client.mutate({
    mutation: gql(updateLiveChannel),
    variables: getEncodedValue({
      id: channel.id,
      name: channel.name,
      code: channel.code,
      number: channel.channelNumber,
      categoryId: channel.channelCategoryID,
      categoryName: channel.categoryName,
      language: channel.channelLanguage,
      description: channel.description,
      externalURL: channel.externalURL,
      tvType: channel.tvType,
      status: channel.status,
      isDRM: channel.isDRM,
      isEpgURL: channel.isEpgURL,
      epgURL: channel.epgURL,
      images: channel.images,
      iptv: channel.iptv,
      coaxial: channel.coaxial,
      bouquet: channel.bouquetInput,
      hotelId: hotelId,
      lang: lang,
      pushChannel: channel.pushChannel,
      swapChannel: channel.swapChannel,
    }),
  });
};

export const deleteTVChannel = async (hotelId, id, lang) => {
  return await propertyServiceV3Client.mutate({
    mutation: gql(deleteLiveChannel),
    variables: getEncodedValue({
      id: id,
      hotelId: hotelId,
      lang: lang,
    }),
  });
};

export const changeTVChannelStatus = async (hotelId, id, status, lang) => {
  return await propertyServiceV3Client.mutate({
    mutation: gql(changeTVStatus),
    variables: getEncodedValue({
      id: id,
      hotelId: hotelId,
      status: status,
      lang: lang,
    }),
  });
};

export const addCityGuide = async (payload, lang) => {
  return await propertyServiceV4Client.mutate({
    mutation: gql(createCityGuide),
    variables: getEncodedValue({
      categoryId: payload?.categoryId,
      hotelId: payload.hotelId,
      name: payload.name,
      code: payload.code,
      description: payload.description,
      distanceFromHotel: payload.distanceFromHotel,
      units: payload.units,
      isActive: payload.isActive,
      location: payload.location,
      information: payload.information,
      duration: payload.duration,
      images: payload.images,
      lang: lang,
      mediaContent: payload.mediaContent,
      customAttributes: payload.customAttributes,
    }),
  });
};

export const editCityGuide = async (payload, lang) => {
  return await propertyServiceV4Client.mutate({
    mutation: gql(updateCityGuide),
    variables: getEncodedValue({
      categoryId: payload?.categoryId,
      id: payload.id,
      hotelId: payload.hotelId,
      name: payload.name,
      code: payload.code,
      description: payload.description,
      distanceFromHotel: payload.distanceFromHotel,
      units: payload.units,
      isActive: payload.isActive,
      location: payload.location,
      information: payload.information,
      duration: payload.duration,
      images: payload.images,
      lang: lang,
      mediaContent: payload.mediaContent,
      customAttributes: payload.customAttributes,
    }),
  });
};

export const getCityGuideList = async (hotelId, lang) => {
  return await propertyServiceV4Client.query({
    query: gql(getCityGuide),
    variables: getEncodedValue({
      hotelId: hotelId,
      lang: lang,
    }),
    fetchPolicy: 'network-only',
  });
};

export const removeCityGuide = async (payload, lang) => {
  return await propertyServiceV4Client.mutate({
    mutation: gql(deleteCityGuide),
    variables: getEncodedValue({
      hotelId: payload.hotelId,
      id: payload.id,
      lang: lang,
    }),
  });
};

export const changeCityGuideState = async (payload, lang) => {
  return await propertyServiceV4Client.mutate({
    mutation: gql(updateCityGuideState),
    variables: getEncodedValue({
      hotelId: payload.hotelId,
      id: payload.id,
      status: payload.status,
      lang: lang,
    }),
  });
};

export const createCityGuideCategory = async (category, hotelId, lang) => {
  return await propertyServiceV4Client.mutate({
    mutation: gql(cityGuideCategory),
    variables: getEncodedValue({
      name: category.name,
      hotelId: hotelId,
      lang: lang,
      customAttributes: category.customAttributes,
    }),
  });
};

export const getCityGuideCategories = async (hotelId, lang) => {
  return await propertyServiceV4Client.query({
    query: gql(cityGuideCategories),
    variables: getEncodedValue({
      hotelId: hotelId,
      lang: lang,
    }),
    fetchPolicy: 'network-only',
  });
};

export const updateCityGuideCategory = async (category, hotelId, lang) => {
  return await propertyServiceV4Client.mutate({
    mutation: gql(updateCityGuidesCategory),
    variables: getEncodedValue({
      name: category.name,
      hotelId: hotelId,
      id: category.id,
      lang: lang,
      customAttributes: category.customAttributes,
    }),
  });
};

export const deleteCityGuideCategory = async (categoryId, hotelId, lang) => {
  return await propertyServiceV4Client.mutate({
    mutation: gql(deleteCityGuidesCategory),
    variables: getEncodedValue({
      id: categoryId,
      hotelId: hotelId,
      lang: lang,
    }),
  });
};

export const addFeedback = async (payload, lang) => {
  return await propertyServiceV4Client.mutate({
    mutation: gql(createFeedback),
    variables: getEncodedValue({
      hotelId: payload.hotelId,
      destination: payload.destination,
      duration: payload.duration,
      pageTitle: payload.pageTitle,
      subtext: payload.subtext,
      isActive: payload.isActive,
      feedbackCategories: payload.feedbackCategories,
      lang: lang,
    }),
  });
};

export const getFeedbackList = async (hotelId, lang) => {
  return await propertyServiceV4Client.query({
    query: gql(listFeedback),
    variables: getEncodedValue({
      hotelId: hotelId,
      lang: lang,
    }),
    fetchPolicy: 'network-only',
  });
};

export const activateFeedbackStatus = async (hotelId, id, lang) => {
  return await propertyServiceV4Client.mutate({
    mutation: gql(activateFeedback),
    variables: getEncodedValue({
      hotelId: hotelId,
      id: id,
      lang: lang,
    }),
  });
};

export const editFeedback = async (payload, lang) => {
  return await propertyServiceV4Client.mutate({
    mutation: gql(updateFeedback),
    variables: getEncodedValue({
      id: payload.id,
      hotelId: payload.hotelId,
      destination: payload.destination,
      duration: payload.duration,
      pageTitle: payload.pageTitle,
      subtext: payload.subtext,
      isActive: payload.isActive,
      feedbackCategories: payload.feedbackCategories,
      lang: lang,
    }),
  });
};

export const deactivateFeedbackStatus = async (hotelId, id, lang) => {
  return await propertyServiceV4Client.mutate({
    mutation: gql(deactivateFeedback),
    variables: getEncodedValue({
      hotelId: hotelId,
      id: id,
      lang: lang,
    }),
  });
};

export const deleteFeedbackDetails = async (hotelId, id, lang) => {
  return await propertyServiceV4Client.mutate({
    mutation: gql(deleteFeedback),
    variables: getEncodedValue({
      hotelId: hotelId,
      id: id,
      lang: lang,
    }),
  });
};

export const getEmailServicesList = async (hotelId, lang) => {
  return await propertyServiceV4Client.query({
    query: gql(getEmailServices),
    variables: getEncodedValue({
      hotelId: hotelId,
      lang: lang,
    }),
    fetchPolicy: 'network-only',
  });
};

export const updateEmailServiceDetails = async (payload, lang) => {
  return await propertyServiceV4Client.mutate({
    mutation: gql(updateEmailService),
    variables: getEncodedValue({
      id: payload.id,
      hotelId: payload.hotelId,
      name: payload.name,
      fromName: payload.fromName,
      fromAddress: payload.fromAddress,
      bcc: payload.bcc,
      cc: payload.cc,
      subject: payload.subject,
      templateType: payload.templateType,
      audience: payload.audience,
      lang: lang,
    }),
  });
};

export const deleteEmailTemplate = async (id, hotelId, lang) => {
  return await propertyServiceV4Client.mutate({
    mutation: gql(deleteEmailServiceTemplate),
    variables: getEncodedValue({
      hotelId: hotelId,
      id: id,
      lang: lang,
    }),
  });
};

export const createEmailServiceTemplate = async (payload, lang) => {
  return await propertyServiceV4Client.mutate({
    mutation: gql(createEmailService),
    variables: getEncodedValue({
      hotelId: payload.hotelId,
      name: payload.name,
      fromName: payload.fromName,
      fromAddress: payload.fromAddress,
      audience: payload.audience,
      subject: payload.subject,
      htmlTemplateBody: payload.htmlTemplateBody || '',
      cc: payload.cc || '',
      bcc: payload.bcc || '',
      lang: lang,
      templateType: payload.templateType || '',
    }),
  });
};

export const updateEmailServiceTemplates = async (payload, lang) => {
  return await propertyServiceV4Client.mutate({
    mutation: gql(updateEmailService),
    variables: getEncodedValue({
      id: payload.id,
      hotelId: payload.hotelId,
      name: payload.name,
      fromName: payload.fromName,
      fromAddress: payload.fromAddress,
      subject: payload.subject,
      templateType: payload.templateType,
      audience: payload.audience,
      htmlTemplateBody: payload.htmlTemplateBody || '',
      cc: payload.cc || '',
      bcc: payload.bcc || '',
      lang,
    }),
  });
};

export const updateEmailServiceWithHtml = async (payload, lang) => {
  return await propertyServiceV4Client.mutate({
    mutation: gql(updateEmailService),
    variables: getEncodedValue({
      id: payload.id,
      hotelId: payload.hotelId,
      name: payload.name,
      fromName: payload.fromName,
      fromAddress: payload.fromAddress,
      subject: payload.subject,
      bcc: payload.bcc || '',
      cc: payload.cc || '',
      htmlTemplateBody: payload.htmlTemplateBody || '',
      isPublished: payload.isPublished || false,
      lang: lang,
      templateType: payload.templateType || '',
      audience: payload.audience,
    }),
  });
};

export const getEmailServiceTemplate = async (hotelId, id, lang) => {
  return await propertyServiceV4Client.query({
    query: gql(getEmailServices),
    variables: getEncodedValue({
      hotelId: hotelId,
      id: id,
      lang: lang,
    }),
    fetchPolicy: 'network-only',
  });
};

export const getEmailTemplateTypes = async (listType = 'templateTypes') => {
  return await propertyServiceV7Client.query({
    query: gql(getlistEmailTemplateVariables),
    variables: getEncodedValue({
      listType,
    }),
    fetchPolicy: 'network-only',
  });
};

export const getDefaultEmailTemplate = async (templateType) => {
  return await propertyServiceV7Client.query({
    query: gql(getDefaultTemplate),
    variables: getEncodedValue({ templateType }),
    fetchPolicy: 'network-only',
  });
};

export const publishEmailTemplate = async (id, hotelId, lang) => {
  return await propertyServiceV4Client.mutate({
    mutation: gql(publishEmailServiceTemplate),
    variables: getEncodedValue({
      hotelId: hotelId,
      id: id,
      lang: lang,
    }),
  });
};

export const unPublishEmailTemplate = async (id, hotelId, lang) => {
  return await propertyServiceV4Client.mutate({
    mutation: gql(unPublishEmailServiceTemplate),
    variables: getEncodedValue({
      hotelId: hotelId,
      id: id,
      lang: lang,
    }),
  });
};

export const getChannelNumberList = async (hotelId, lang) => {
  return await propertyServiceV4Client.query({
    query: gql(listChannnelCount),
    variables: getEncodedValue({
      hotelId: hotelId,
      lang: lang,
    }),
    fetchPolicy: 'network-only',
  });
};

export const uploadBulkTvChannelsByCSV = async (payload, lang) => {
  return await propertyServiceV4Client.mutate({
    mutation: gql(uploadTvChannelsByCSV),
    variables: getEncodedValue({
      IsReplace: payload.isReplace,
      hotelId: payload.hotelId,
      tvChannels: payload.tvChannels,
      lang: lang,
    }),
  });
};

export const uploadBulkTvBouquestsByCSV = async (payload, lang) => {
  return await propertyServiceV4Client.mutate({
    mutation: gql(uploadBouquetsByCSV),
    variables: getEncodedValue({
      IsReplace: payload.isReplace,
      hotelId: payload.hotelId,
      bouquets: payload.bouquets,
      lang: lang,
    }),
  });
};

export const uploadBulkCategoriesByCSV = async (payload, lang) => {
  return await propertyServiceV4Client.mutate({
    mutation: gql(uploadCategoriesByCSV),
    variables: getEncodedValue({
      IsReplace: payload.isReplace,
      hotelId: payload.hotelId,
      categories: payload.categories,
      lang: lang,
    }),
  });
};
